import { SetTuningResponse } from '../models/response/SetTuningResponse'
import { TuningInfoDto, TuningWraperDto } from '../models/TuningDto'
import { Vehicle } from '../models/Vehicle'
import { FineTuningPutRequest, FineTuningVehicle } from '../pages/fineTuning/FineTuning.types'
import { PickVehicleResponse } from '../pages/main/MainPage.types'
import { VehicleSpecData } from '../pages/main/vehicleSpec/VehicleSpec.types'
import { VehicleResearchResponse } from '../types/vehicle'
import { api } from './api'

interface VehiclesResponse {
    vehicles: Vehicle[]
    availableBrands: string[]
    availableRanks: number[]
}

export interface VehicleRequest {
    brand?: string
    rank?: number
    type?: string
    name?: string
}

export type UserVehicleStates = "NO_RELATION" | "BOUGHT" | "RESEARCHED" | "RESEARCHABLE"

export class VehicleService {
    static async fetchResearchTree(userId: number, branch: string): Promise<VehicleResearchResponse[]> {
        const response = await api.get(`/vehicle/research?userId=${userId}&branch=${branch}`)
        return response.data
    }

    static async buyVehicle(vehicleId: number): Promise<void> {
        await api.post('/vehicle/buy', { vehicleId })
    }

    static async researchVehicle(vehicleId: number): Promise<void> {
        await api.post('/vehicle/research', { vehicleId })
    }

    static async getUserVehicles(vehicleRequest: VehicleRequest): Promise<VehiclesResponse> {
        const response = await api.get(`/vehicles?brand=${vehicleRequest.brand || ''}&rank=${vehicleRequest.rank || ''}&type=${vehicleRequest.type || ''}&name=${vehicleRequest.name || ''}`)
        return response.data as VehiclesResponse
    }

    static async pickVehicle(userId: number, vehicleId: number): Promise<PickVehicleResponse> {
        const response = await api.post('/vehicle/lobby/pick', { userId, vehicleId })
        return response.data as PickVehicleResponse
    }

    static async getAvailableTunings(carId: number): Promise<TuningWraperDto> {
        const response = await api.get(`/vehicle/tuning?vehicleId=${carId}`)
        return response.data as TuningWraperDto
    }

    static async setTuning(carId: number, partId: number, tuningId: number, typeId: number): Promise<SetTuningResponse> {
        const response = await api.post('/vehicle/setTuning', { vehicleId: carId, partId: partId, tuningId: tuningId, typeId: typeId })
        return response.data as SetTuningResponse
    }

    static async buyTuning(vehicleId: number, partId: number, tuningId: number, typeId: number): Promise<void> {
        await api.post('/vehicle/tuning/buy', { vehicleId, partId, tuningId, typeId })
    }

    static async equipTuning(vehicleId: number, partId: number, tuningId: number, typeId: number): Promise<void> {
        await api.post('/vehicle/tuning/equip', { vehicleId, partId, tuningId, typeId })
    }

    static async destroyLobbyVehicle(): Promise<void> {
        await api.delete('/vehicle/lobby/destroy')
    }

    static async setColor(vehicleId: number, isColor1: boolean, r: number, g: number, b: number): Promise<number> {
        const response = await api.post('/vehicle/tuning/color/set', { vehicleId, isColor1, r, g, b })
        return response.data as number
    }

    static async getSelectedTuningInfo(vehicleId: number, partId: number, tuningId: number, typeId: number): Promise<TuningInfoDto> {
        const response = await api.get(`/vehicle/tuning/info?vehicleId=${vehicleId}&partId=${partId}&tuningId=${tuningId}&typeId=${typeId}`)
        return response.data as TuningInfoDto
    }

    static async getFineTuning(vehicleId: number): Promise<FineTuningVehicle> {
        const response = await api.get(`/vehicle/finetuning?vehicleId=${vehicleId}`)
        return response.data as FineTuningVehicle
    }

    static async storeFineTuningSettings(request: FineTuningPutRequest): Promise<void> {
        await api.put('/vehicle/finetuning', request)
    }

    static async purgeGameVehicleHandling(vehicleId: number): Promise<void> {
        await api.delete(`/vehicle/game/handling/purge?vehicleId=${vehicleId}`)
    }

    static async payoffVehicleDamage(vehicleId: number): Promise<void> {
        await api.post('/vehicle/damage/payoff', { vehicleId })
    }

    static async getVehicle(vehicleId: number): Promise<Vehicle> {
        const response = await api.get(`/vehicle?vehicleId=${vehicleId}`)
        return response.data as Vehicle
    }

    static async getVehicleModelNameById(vehicleId: number): Promise<string> {
        const response = await api.get(`/vehicle/model-name?vehicleId=${vehicleId}`)
        return response.data as string
    }

    static async getUserVehicleState(vehicleId: number): Promise<UserVehicleStates> {
        const response = await api.get(`/vehicle/user/state?vehicleId=${vehicleId}`)
        return response.data as UserVehicleStates
    }

    static async getVehiclePrice(vehicleId: number): Promise<number> {
        const response = await api.get(`/vehicle/price?vehicleId=${vehicleId}`)
        return response.data as number
    }

    static async getVehicleResearchPrice(vehicleId: number): Promise<number> {
        const response = await api.get(`/vehicle/research/price?vehicleId=${vehicleId}`)
        return response.data as number
    }

    static async getVehicleRaceExperience(vehicleId: number): Promise<number> {
        const response = await api.get(`/vehicle/race/experience?vehicleId=${vehicleId}`)
        return response.data as number
    }

    static async fetchVehicleStats(modelName: string): Promise<VehicleSpecData> {
        const response = await api.get(`/vehicle/settings/l2?modelName=${modelName}`)
        return response.data as VehicleSpecData
    }

    static async previewVehicle(vehicleId: number): Promise<void> {
        await api.post('/vehicle/preview', { vehicleId })
    }

    static async runTestDrive(
        vehicleId: number,
        color1: { r: number, g: number, b: number },
        color2: { r: number, g: number, b: number }
    ): Promise<void> {
        await api.post('/vehicle/test-drive', { vehicleId, color1, color2 })
    }

    static async leaveTestDrive(): Promise<void> {
        await api.post('/vehicle/test-drive/leave')
    }
}